import isEmpty from 'lodash/isEmpty'

export const DEFAULT_ERROR_MSG =
  'An unexpected error occurred. Please try again later or contact support.'

/**
 * Keys of the error object that might contain the error message.
 * Unfortunately, the API responses aren't standardised.
 */
const ERROR_MESSAGE_KEYS = Object.freeze([
  'message',
  'error_message',
  'error',
  'msg',
  'Response',
  'response',
])

/**
 * Gets the error message from an error object of unknown type.
 * @param error Error object of unknown type.
 * @param alt Alternative error message if the error is falsy.
 * @returns Error message
 */
export const getErrorMessage = (error: unknown, alt = DEFAULT_ERROR_MSG): string => {
  if (!error) return alt

  if (typeof error === 'string') {
    // Check if it's a stringified JSON. If so, re-run the function on the parsed object.
    try {
      const parsed = JSON.parse(error)
      return getErrorMessage(parsed, alt)
    } catch (_error) {
      // It's a string, return it as the error message.
      return error
    }
  }

  if (typeof error === 'object' && error) {
    try {
      const obj = error as Record<string, unknown>

      // Check if the error object has a truthy string value for any of the error message keys.
      for (const key of ERROR_MESSAGE_KEYS)
        if (key in obj && obj[key] && typeof obj[key] === 'string') return obj[key] as string

      // Check nested vtiger-style payloads such as { err: { msg: '...' } }.
      if ('err' in obj && typeof obj.err === 'object' && obj.err) {
        const nestedErr = obj.err as Record<string, unknown>
        if (typeof nestedErr.msg === 'string' && nestedErr.msg) return nestedErr.msg
        if (typeof nestedErr.message === 'string' && nestedErr.message) return nestedErr.message
      }

      if (!isEmpty(error)) return JSON.stringify(error, null, 2)
    } catch (error) {
      console.warn('Failed to extract error message from non-null error of type "object"')
      console.error(error)
    }
  }

  return alt
}
